import React, { FormEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { requestMagicLinkAction } from "@/features/auth/slices/authSlice";
import { AppDispatch } from "@/app/store";
import { RootState } from "@/app/rootReducer";
import { useNavigate } from "react-router-dom";

// shadcn UI components (or your own)
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Local assets
import nscsLogoColor from "@/assets/images/nscs_logo_color.svg";
import loginBgImage from "@/assets/images/login_bg.jpg";

const Login: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const {
    magicLinkLoading,
    magicLinkError,
    // magicLinkSuccess,
    // magicLinkMessage,
  } = useSelector((state: RootState) => state.auth);

  const [email, setEmail] = useState("");

  // Basic email validation (very simple pattern)
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    dispatch(
      requestMagicLinkAction(email, {
        onSuccess: () => {
          // If requestMagicLinkAction was successful, redirect here:
          navigate("/login/magic-link-sent");
        },
      }),
    );
  };

  return (
    <div className="flex min-h-screen">
      {/* Left side: 50% width image (hidden on smaller screens) */}
      <div className="relative hidden w-1/2 lg:block">
        <div className="relative h-full w-full">
          <img
            alt="NSCS Portal Background"
            className="absolute inset-0 h-full w-full object-cover"
            src={loginBgImage}
          />
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative z-10 flex h-full flex-col items-center justify-center p-8 text-white">
            <h1 className="mb-4 text-3xl font-bold">
              Welcome to the NSCS Chapter Portal
            </h1>
            <p className="max-w-md text-center text-lg">
              Empowering the next generation of leaders.
            </p>
          </div>
        </div>
      </div>

      {/* Right side: login form (50% on large screens, full width otherwise) */}
      <div className="flex w-full flex-1 flex-col justify-center bg-white px-4 py-12 shadow-md sm:px-6 lg:w-1/2 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96 relative">
          {/* Logo & Title */}
          <div>
            <img alt="NSCS Logo" className="h-10 w-auto" src={nscsLogoColor} />
            <h2 className="mt-8 text-2xl font-bold tracking-tight text-gray-900">
              Sign in to your account
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              Need help accessing your account?{" "}
              <a
                className="font-semibold text-primary hover:text-primary-light"
                href="#"
              >
                Contact Support
              </a>
            </p>
          </div>

          {/* Form */}
          <div className="mt-10">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Email */}
              <div>
                <label
                  className="block text-sm font-medium text-gray-900"
                  htmlFor="email"
                >
                  Email address
                </label>
                <div className="mt-2">
                  <Input
                    autoComplete="email"
                    id="email"
                    name="email"
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jane.doe@university.edu"
                    required
                    type="email"
                    value={email}
                  />
                </div>
              </div>

              {/* Submit */}
              <div>
                <Button
                  className="flex w-full justify-center rounded-md bg-primary px-3 py-1.5 text-base font-semibold text-white shadow-sm hover:bg-primary-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-light"
                  disabled={!isEmailValid || magicLinkLoading}
                  type="submit"
                >
                  {magicLinkLoading
                    ? "Requesting..."
                    : "Send me a sign-in link"}
                </Button>
              </div>
            </form>

            {/* Error Messages */}
            {magicLinkError && (
              <p className="mt-4 text-center text-sm font-semibold text-red-600">
                {magicLinkError}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
